import { watchDebounced } from '@vueuse/core';
import { FuseOptions, useFuse } from '@vueuse/integrations/useFuse.mjs';
import {
  computed, ComputedRef, isRef, MaybeRef, MaybeRefOrGetter, Ref, ref,
  toValue,
} from 'vue';

import { i18n } from '@/i18n';

export interface DropdownSearcher<T> {
  (records: MaybeRef<T[]>, labelKey: keyof T | undefined): {
    filterMethod: (searchString: string) => void;
    filtered: ComputedRef<T[]> | Ref<T[]>;
    searchString: Ref<string>;
    isLoading: Ref<boolean>;
  };
}

export function getFuseDropdownSearcher<T>(fuseOptions: FuseOptions<T> = { threshold: 0.1 }): DropdownSearcher<T> {
  return (records, labelKey) => {
    const searchString = ref('');

    const defaultKeys = [{
      name: 'label',
      getFn: (item: any) => {
        if (!item[labelKey || 'label']) return '';
        if (!i18n.global.te(item[labelKey || 'label'])) return item[labelKey || 'label'];
        return i18n.global.t(item[labelKey || 'label']);
      },
    }];

    const { results } = useFuse<T>(searchString, records, {
      fuseOptions: {
        ...fuseOptions,
        keys: fuseOptions.keys ?? defaultKeys,
      },
      matchAllWhenSearchEmpty: true,
    });

    return {
      filterMethod: (newSearchString: string) => {
        searchString.value = newSearchString;
      },
      filtered: computed(() => results.value.map((result) => result.item)),
      searchString,
      isLoading: ref(false),
    };
  };
}

interface GetApiDropdownSearcherOptions {
  debounce: MaybeRefOrGetter<number>;
  maxWait?: MaybeRefOrGetter<number>;
}
export function getApiDropdownSearcher<T>(
  onChangeFilter: (searchString: string) => Promise<void>,
  options: GetApiDropdownSearcherOptions = { debounce: 500, maxWait: 1000 },
): DropdownSearcher<T> {
  return (records) => {
    const searchString = ref('');
    const isLoading = ref(false);

    watchDebounced(searchString, async () => {
      isLoading.value = true;
      await onChangeFilter(searchString.value);
      isLoading.value = false;
    }, options);

    return {
      filterMethod: (newSearchString: string) => {
        searchString.value = newSearchString;
      },
      filtered: isRef(records)
        ? records
        : computed(() => records),
      searchString,
      isLoading,
    };
  };
}

export function getCaseInsensitiveDropdownSearcher<T extends { label?: string }>(): DropdownSearcher<T> {
  return (records, labelKey) => {
    const searchString = ref('');

    const filtered = computed(() => {
      if (!searchString.value) return toValue(records);
      return toValue(records).filter(
        (record) => {
          const label = record[labelKey || 'label'];
          if (!label || typeof label !== 'string') return false;
          return label.toLowerCase().includes(searchString.value.toLowerCase());
        },
      );
    });

    return {
      filterMethod: (newSearchString: string) => {
        searchString.value = newSearchString;
      },
      filtered,
      searchString,
      isLoading: ref(false),
    };
  };
}
